import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Typography, Tooltip, useTheme } from '@mui/material';
import useIsContained from '../../hooks/useIsContained';
import {
  getFirstLettersOfName,
  capitalizeWords,
  distinctArray,
  getAvatarColors,
} from '../../utils/common';
import { Flex, FlexCenter } from '../Flex';
import OverflowTooltip from '../OverflowTooltip';
import { fontSize, fontWeight, radius, space } from '../../style/utils';

const AVATAR_PADDING_RIGHT = '0.3px';
const INITIALS_LINE_HEIGHT = '8px';

const Wrapper = styled(Flex)`
  position: relative;
  align-items: center;
  gap: ${space('sm')};
`;

const Circle = styled(FlexCenter)<{ color: string }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border-radius: ${radius('round')};
  align-items: center;
  padding-right: ${AVATAR_PADDING_RIGHT};
  background: ${({ color }) => color};
`;

type NameCircleProps = {
  name: string;
  parentRef: any;
  onContained: () => void;
  onUnContained: () => void;
};

const NameCircle = ({
  name,
  parentRef,
  onContained,
  onUnContained,
}: NameCircleProps) => {
  const theme = useTheme();
  const avatarColors = getAvatarColors(name, theme);
  const childRef = useRef<any>();
  const isContained = useIsContained(parentRef, childRef);

  useEffect(() => {
    if (isContained) {
      onContained();
    } else {
      onUnContained();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContained]);

  return (
    <Circle
      ref={childRef}
      sx={{ opacity: isContained ? 1 : 0 }}
      color={avatarColors.background}
    >
      <Typography
        sx={{
          fontSize: fontSize('xxxs'),
          fontWeight: fontWeight('semibold'),
          lineHeight: INITIALS_LINE_HEIGHT,
          color: avatarColors.text,
        }}
      >
        {getFirstLettersOfName(name).toUpperCase()}
      </Typography>
    </Circle>
  );
};

const UserNameRenderer = ({ names }: { names: string[] }) => {
  const theme = useTheme();
  const parentRef = useRef<any>();
  const [unContainedItems, setUnContainedItems] = useState<string[]>([]);

  const handleUnContained = (id: string) => {
    setUnContainedItems((prev) => distinctArray([...prev, id]));
  };

  const handleContained = (id: string) => {
    setUnContainedItems((prev) => prev.filter((itemId) => itemId !== id));
  };

  return (
    <Wrapper ref={parentRef}>
      {names.map((name, i) => (
        <Tooltip
          key={i}
          title={name}
          open={names.length > 1 ? undefined : false}
        >
          <Flex>
            <NameCircle
              name={name}
              parentRef={parentRef}
              onContained={() => handleContained(i.toString())}
              onUnContained={() => handleUnContained(i.toString())}
            />
          </Flex>
        </Tooltip>
      ))}

      {unContainedItems.length > 0 && (
        <Circle
          sx={{
            position: 'absolute',
            left: (names.length - unContainedItems.length) * 28,
          }}
          color={theme.palette.surface.hover}
        >
          <Typography
            sx={{
              fontSize: fontSize('xxxs'),
              fontWeight: fontWeight('semibold'),
              lineHeight: INITIALS_LINE_HEIGHT,
              color: 'content.primary',
            }}
          >
            {`+${unContainedItems.length}`}
          </Typography>
        </Circle>
      )}

      {names.length === 1 && (
        <OverflowTooltip>{capitalizeWords(names[0])}</OverflowTooltip>
      )}
    </Wrapper>
  );
};

// a wrapper component in order to provide a key to UserNameRenderer.
// the key is used to force rerender on the whole component when names prop is changing
// without rerendering there are optimization problems when previous state is left from the previous names prop.
const WrapperComponent = (names: string[]) => (
  <UserNameRenderer key={names.toString()} names={names} />
);

export default WrapperComponent;
